import React, { useEffect, useState } from 'react';
import { MinusCircleOutlined, PlusOutlined, UpCircleOutlined, DownCircleOutlined } from '@ant-design/icons';
import { Layout, message, Button, Form, Input, Switch, Select, Tabs, Drawer, Row, Col } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { useNavigate, useParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch, useSelector } from 'react-redux';
import dot from 'dot-object';
import MediaSelect from './components/mediaSelect.field';
import EntityMedia from './entity.media.page';
import { API_CDN_URL } from '../services/config';
import {
    readTripCountry,
    createTripCountry,
    updateTripCountry
} from '../actions/entity.trip.country'
import { userIsReadOnlyInEntity } from '../helpers/user';
import { useTranslation } from 'react-i18next';
import SeoFields from './components/seo.component';
import Editor from './components/editor.field';

const layout = {
    labelCol: {
        span: 8,
    },
    wrapperCol: {
        span: 10,
    },
};

function TripCountry() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const entities = useSelector(state => state.entity.entities);
    const currentEntity = entities ? entities.find(entity => entity.id === params.entityId) : null;
    const { Content } = Layout;
    const [form] = Form.useForm();
    const [entityCountryTitle, setTripCountryTitle] = useState('');
    const currentUser = useSelector(state => state.auth.user);
    const userIsReadOnly = userIsReadOnlyInEntity(currentUser, currentEntity);
    const requiredTripSupportAllLanguages = currentEntity && currentEntity.requiredTripSupportAllLanguages;
    const [addedLanguages, setAddedLanguages] = useState([]);
    const [mediaDrawerVisible, setMediaDrawerVisible] = useState(false);
    const [currentMediaFieldName, setCurrentMediaFieldName] = useState(null);
    const [countryData, setCountryData] = useState(null);

    const showMediaDrawer = (fieldName) => {
        console.log(fieldName);
        setCurrentMediaFieldName(fieldName);
        setMediaDrawerVisible(true);
    };
    const onMediaDrawerClose = () => {
        setMediaDrawerVisible(false);
    };

    const onSelectMedia = (media) => {
        setMediaDrawerVisible(false);
        if (currentMediaFieldName && currentEntity) {
            let url = API_CDN_URL(currentEntity) + media.path;
            let newValue = {
                [currentMediaFieldName.join('.')]: url
            };
            let formValues = form.getFieldsValue();
            // Use dot to update partial values
            newValue = dot.dot(newValue);
            formValues = dot.dot(formValues);
            newValue = { ...formValues, ...newValue };
            newValue = dot.object(newValue);
            form.setFieldsValue(newValue);
        }
    }

    useEffect(() => {
        if (currentEntity) {
            if (currentEntity.requiredTripSupportAllLanguages) {
                setAddedLanguages(currentEntity.languages.filter(language => {
                    return language.status
                }).map(language => {
                    return {
                        label: language.name,
                        value: language.code
                    }
                }));
            } else {
                if (countryData && typeof countryData.languages !== 'undefined' && Array.isArray(countryData.languages)) {
                    setAddedLanguages(currentEntity.languages.filter(language => {
                        return language.status && countryData.languages.includes(language.code)
                    }).map(language => {
                        return {
                            label: language.name,
                            value: language.code
                        }
                    }));
                }
            }
        }
    }, [currentEntity, countryData]);
    const languageOptions = !requiredTripSupportAllLanguages && currentEntity && currentEntity.languages ? currentEntity.languages.map(language => {
        return {
            label: language.name,
            value: language.code,
            disabled: !language.status
        }
    }) : [];

    const onFinish = (values) => {
        if (userIsReadOnly) {
            message.error(t('tripCountry.message.read-only'));
            return;
        }
        // Loop languages and check its features and images
        addedLanguages.forEach(language => {
            if (typeof values[language.value].features === 'undefined') {
                values[language.value].features = [];
            }
            if (typeof values[language.value].images === 'undefined') {
                values[language.value].images = [];
            }
        });

        if (typeof params.countryId !== 'undefined') {
            // Update the existing trip country
            dispatch(updateTripCountry(params.entityId, params.countryId, values)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripCountryTitle(t('tripCountry.title.edit'));
                    form.setFieldsValue(response.result);
                    setCountryData(response.result);
                }
                message.success(t('tripCountry.message.update-success'));
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        } else {
            // Create new campaign
            dispatch(createTripCountry(params.entityId, values)).then((data) => {
                message.success(t('tripCountry.message.create-success'));
                if (data.result) {
                    navigate(`/entity/${params.entityId}/trip-country/${data.result.id}`);
                } else {
                    navigate(`/entity/${params.entityId}/trip-countries`);
                }
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        }
    }

    useEffect(() => {
        if (typeof params.countryId !== 'undefined') {
            setTripCountryTitle(t('tripCountry.title.edit'));
            dispatch(readTripCountry(params.entityId, params.countryId)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripCountryTitle(t('tripCountry.title.edit'));
                    form.setFieldsValue(response.result);
                    setCountryData(response.result);
                } else {
                    message.error(t('tripCountry.message.error-reading'));
                    navigate(`/entity/${params.entityId}/trip-countries`);
                }
            }).catch(e => {
                message.error(t('tripCountry.message.error-reading'));
                navigate(`/entity/${params.entityId}/trip-countries`);
            });
        } else {
            setTripCountryTitle(t('tripCountry.title.new'));
        }
    }, [params.countryId, params.entityId, dispatch, navigate, form]);

    const onFinishFailed = (errorInfo) => {
        if (typeof errorInfo.errorFields !== 'undefined') {
            errorInfo.errorFields.forEach(field => {
                message.error(field.errors[0]);
            });
        }
    }

    const selectedLanguages = (options) => {
        if (Array.isArray(options)) {
            const filteredLanguages = languageOptions.filter(language => {
                if (options.includes(language.value)) {
                    return true;
                } else {
                    return false;
                }
            });
            console.log(options);
            console.log(filteredLanguages);
            setAddedLanguages(filteredLanguages);
        } else {
            setAddedLanguages([]);
        }
    }

    let extraButtons = [
        <Button key="cancel" type="primary" danger href={`/entity/${params.entityId}/trip-countries`}>{t('tripCountry.actions.cancel')}</Button>
    ];
    if (!userIsReadOnly) {
        extraButtons.push(<Button key="save" type="primary" htmlType="submit">{params.countryId ? t('tripCountry.actions.update') : t('tripCountry.actions.save')}</Button>);
    }
    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <Form
                        {...layout}
                        form={form}
                        name="entity-country"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={{
                            status: true
                        }}>
                        <PageHeader
                            className="site-page-header"
                            onBack={() => navigate(`/entity/${params.entityId}/trip-countries`)}
                            title={entityCountryTitle}
                            extra={extraButtons}
                        />
                        <>
                            <Form.Item
                                label={t('tripCountry.field.status.label')}
                                name="status"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('tripCountry.field.slug.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="slug"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripCountry.field.slug.required')
                                        },
                                        { pattern: /^[a-z0-9-_]+$/, message: t('tripCountry.field.slug.pattern') }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('tripCountry.field.slug.placeholder')} />
                                </Form.Item>
                                <div>{t('tripCountry.field.slug.note')}</div>
                            </Form.Item>
                            {!requiredTripSupportAllLanguages && <Form.Item
                                label={t('tripCountry.field.languages.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="languages"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripCountry.field.languages.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Select mode="multiple" allowClear options={languageOptions} onChange={selectedLanguages} />
                                </Form.Item>
                                <div>{t('tripCountry.field.languages.note')}</div>
                            </Form.Item>}
                            <Tabs>
                                {addedLanguages.map((language, index) => {
                                    return <Tabs.TabPane tab={language.label} key={language.value} forceRender>
                                        <Form.Item
                                            label={t('tripCountry.field.name.label')}
                                            name={[language.value, 'name']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('tripCountry.field.name.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('tripCountry.field.name.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripCountry.field.title.label')}
                                            name={[language.value, 'title']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('tripCountry.field.title.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('tripCountry.field.title.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripCountry.field.subtitle.label')}
                                            name={[language.value, 'subtitle']}
                                        >
                                            <Input placeholder={t('tripCountry.field.subtitle.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripCountry.field.summary.label')}
                                            name={[language.value, 'summary']}
                                        >
                                            <Input placeholder={t('tripCountry.field.summary.placeholder')} />
                                        </Form.Item>
                                        <MediaSelect showMediaDrawer={showMediaDrawer} form={form} wrapper={{
                                            wrapperCol: 16,
                                            label: t('tripCountry.field.backgroundImage.label')
                                        }} inner={{
                                            name: [language.value, 'backgroundImage'],
                                        }} />
                                        <Form.List name={[language.value, "images"]} style={{ width: '100%' }}>
                                            {(fields, { add, remove, move }) => (
                                                <>
                                                    <h1>{t('tripCountry.field.images.label')}</h1>
                                                    {fields.map(({ key, name, ...restField }, fieldPosition) => {
                                                        return <div key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                                            <MediaSelect showMediaDrawer={showMediaDrawer} form={form} wrapper={{
                                                                wrapperCol: 16,
                                                                required: true,
                                                                style: { marginBottom: 8, width: 'calc(100% - 100px)' }
                                                            }} inner={{
                                                                name: [name],
                                                                rules: [
                                                                    {
                                                                        required: true,
                                                                        message: t('tripCountry.field.images.required')
                                                                    }
                                                                ]
                                                            }}
                                                                fieldName={[language.value, "images", name]}
                                                                placeholder={t('tripCountry.field.images.placeholder')} />
                                                            <div style={{ width: 100 }}>
                                                                {(fields.length > 1 && fieldPosition !== 0) && <UpCircleOutlined onClick={() => move(fieldPosition, fieldPosition - 1)} style={{ marginLeft: 8 }} />}
                                                                {(fields.length > 1 && fieldPosition !== (fields.length - 1)) && <DownCircleOutlined onClick={() => move(fieldPosition, fieldPosition + 1)} style={{ marginLeft: 8 }} />}
                                                                <MinusCircleOutlined onClick={() => remove(name)} style={{ marginLeft: 8 }} />
                                                            </div>
                                                        </div>
                                                    })}
                                                    <Form.Item wrapperCol={{ span: 24 }}>
                                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                            {t('tripCountry.field.images.add')}
                                                        </Button>
                                                    </Form.Item>
                                                </>
                                            )}
                                        </Form.List>
                                        <Form.List name={[language.value, "features"]} style={{ width: '100%' }}>
                                            {(fields, { add, remove, move }) => (
                                                <>
                                                    <h1>{t('tripCountry.field.features.label')}</h1>
                                                    {fields.map(({ key, name, ...restField }, fieldPosition) => {
                                                        return <div key={key} style={{ display: 'flex', width: '100%' }} align="baseline">
                                                            <Row gutter={[16, 0]} style={{ width: 'calc(100% - 100px)' }}>
                                                                <Col span={12}>
                                                                    <MediaSelect showMediaDrawer={showMediaDrawer} form={form} wrapper={{
                                                                        wrapperCol: 16,
                                                                        style: { marginBottom: 8 }
                                                                    }} inner={{
                                                                        name: [name, "icon"],
                                                                    }}
                                                                        fieldName={[language.value, "features", name, "icon"]}
                                                                        placeholder={t('tripCountry.field.featureIcon.placeholder')} />
                                                                </Col>
                                                                <Col span={12}>
                                                                    <Form.Item
                                                                        {...restField}
                                                                        required={true}
                                                                        name={[name, "title"]}
                                                                        style={{ marginBottom: 8 }}
                                                                        wrapperCol={{ span: 24 }}
                                                                        rules={[{ required: true, message: t('tripCountry.field.featureTitle.required') }]}
                                                                    >
                                                                        <Input placeholder={t('tripCountry.field.featureTitle.placeholder')} />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                            <div style={{ width: 100 }}>
                                                                {(fields.length > 1 && fieldPosition !== 0) && <UpCircleOutlined onClick={() => move(fieldPosition, fieldPosition - 1)} style={{ marginLeft: 8 }} />}
                                                                {(fields.length > 1 && fieldPosition !== (fields.length - 1)) && <DownCircleOutlined onClick={() => move(fieldPosition, fieldPosition + 1)} style={{ marginLeft: 8 }} />}
                                                                <MinusCircleOutlined onClick={() => remove(name)} style={{ marginLeft: 8 }} />
                                                            </div>
                                                        </div>
                                                    })}
                                                    <Form.Item wrapperCol={{ span: 24 }}>
                                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                            {t('tripCountry.field.features.add')}
                                                        </Button>
                                                    </Form.Item>
                                                </>
                                            )}
                                        </Form.List>
                                        <Editor
                                            label={t('tripCountry.field.content.label')}
                                            language={language.value}
                                            name={[language.value, 'content']}
                                            showMediaDrawer={showMediaDrawer}
                                            form={form}
                                        />
                                        <SeoFields
                                            name={[language.value]}
                                            layout={layout}
                                            form={form}
                                            showMediaDrawer={showMediaDrawer}
                                        />
                                    </Tabs.TabPane>
                                })}
                            </Tabs>
                        </>
                    </Form>
                    <Drawer contentWrapperStyle={{ width: '1000px', maxWidth: '100%', minWidth: '50%' }} title={t('mediaDrawer.title')} placement="right" onClose={onMediaDrawerClose} open={mediaDrawerVisible}>
                        <EntityMedia entityId={params.entityId} selectMedia={onSelectMedia} />
                    </Drawer>
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default TripCountry;